/** Cross-cutting constants. See FRD §2.3 and the Global Conventions in documents/task-specs/00-INDEX.md. */

export const APP_TIMEZONE = 'Asia/Kolkata';
export const CURRENCY_CODE = 'INR';
export const CURRENCY_SYMBOL = '₹';

/** FR-CALC-008 — half-up to 2 decimals at every stage. */
export const MONEY_SCALE = 2;

/**
 * Display format: day/month/year, the Indian convention, for screens, PDFs, exports and
 * reminder messages alike. The FRD §2.3 specified DD-MMM-YYYY; this is a later decision.
 *
 * Dates still travel over the API as ISO-8601 (yyyy-MM-dd) and are stored that way — this
 * governs display only, so nothing has to be reparsed from a localised string.
 */
export const DATE_DISPLAY_FORMAT = 'dd/MM/yyyy';
export const DATETIME_DISPLAY_FORMAT = 'dd/MM/yyyy HH:mm';

export const API_PREFIX = '/api/v1';

export const PAGINATION = {
  defaultPage: 1,
  defaultLimit: 20,
  maxLimit: 100,
} as const;

/** Settings keys seeded in Task 02 and managed in Task 23. */
export const SETTING_KEYS = {
  companyName: 'company_name',
  companyLogoPath: 'company_logo_path',
  companyAddressLine1: 'company_address_line1',
  companyAddressLine2: 'company_address_line2',
  companyCity: 'company_city',
  companyState: 'company_state',
  companyPincode: 'company_pincode',
  companyPhone: 'company_phone',
  companyEmail: 'company_email',
  companyWebsite: 'company_website',

  bankAccountName: 'bank_account_name',
  bankAccountNumber: 'bank_account_number',
  bankName: 'bank_name',
  bankBranch: 'bank_branch',
  bankIfsc: 'bank_ifsc',
  bankUpiId: 'bank_upi_id',

  invoicePrefix: 'invoice_prefix',
  billPrefix: 'bill_prefix',
  receiptPrefix: 'receipt_prefix',
  sequencePadding: 'sequence_padding',

  defaultTermsText: 'default_terms_text',
  paymentDueDays: 'payment_due_days',
  discountThresholdPercent: 'discount_threshold_percent',

  eventTypes: 'event_types',
  retentionMonths: 'retention_months',
  retentionNoticeText: 'retention_notice_text',

  paymentReminderSchedule: 'payment_reminder_schedule',
  retentionReminderSchedule: 'retention_reminder_schedule',
  remindersEnabled: 'reminders_enabled',
  smsChannelEnabled: 'sms_channel_enabled',
} as const;

/** BR-08 / FR-SET-007 defaults. The retention period is a setting, never a hard-coded literal. */
export const DEFAULTS = {
  retentionMonths: 12,
  discountThresholdPercent: 10,
  paymentDueDays: 7,
  sequencePadding: 4,
  invoicePrefix: 'INV',
  billPrefix: 'BILL',
  receiptPrefix: 'RCPT',
  /** FR-NOTIF-002 — days relative to due date. Negative is before. */
  paymentReminderSchedule: [-3, 0, 7, 15, 30],
  /** FR-NOTIF-003 — days before retention expiry. */
  retentionReminderSchedule: [-30, -7],
} as const;

/** Dues ageing buckets, FR-DASH-003. */
export const AGEING_BUCKETS = ['0-30', '31-60', '61-90', '90+'] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];

/** API error codes, FRD §10. */
export const ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  BUSINESS_RULE: 'BUSINESS_RULE',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
